import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { z } from "zod";

// ABN validation helper - strips spaces/dashes and validates 11 digits
const normalizeAbn = (abn: string | undefined): string => {
  if (!abn) return "";
  return abn.replace(/[\s-]/g, "");
};

const tenancyFormSchema = z.object({
  tenantName: z.string().min(1, "Tenant name is required"),
  tenantType: z.string().min(1, "Tenant type is required"),
  tenantAbn: z.string().optional().refine(
    (val) => {
      if (!val || val.trim() === "") return true; // Optional field
      const normalized = normalizeAbn(val);
      return /^\d{11}$/.test(normalized); // Must be exactly 11 digits
    },
    { message: "ABN must be 11 digits" }
  ),
  leaseStartDate: z.string().min(1, "Lease start date is required"),
  leaseEndDate: z.string().optional(), // Made optional for vacant tenancies
  currentRentAnnual: z.string().min(1, "Annual rent is required"),
  tenancyAreaSqm: z.string().optional(),
  isVacant: z.boolean().optional(),
}).refine(
  (data) => {
    // Skip date validation if vacant or no end date
    if (data.isVacant || !data.leaseEndDate || data.leaseEndDate.trim() === "") {
      return true;
    }
    // Lease end date must be after start date
    return new Date(data.leaseEndDate) > new Date(data.leaseStartDate);
  },
  {
    message: "Lease end date must be after start date",
    path: ["leaseEndDate"],
  }
).refine(
  (data) => {
    // If not vacant, lease end date is required
    if (!data.isVacant && (!data.leaseEndDate || data.leaseEndDate.trim() === "")) {
      return false;
    }
    return true;
  },
  {
    message: "Lease end date is required",
    path: ["leaseEndDate"],
  }
);

type TenancyFormData = z.infer<typeof tenancyFormSchema>;

interface Tenancy {
  id: string;
  securityPropertyId: string;
  tenantName: string;
  tenantType: string;
  tenantAbn: string | null;
  leaseStartDate: string;
  leaseEndDate: string;
  currentRentAnnual: string;
  tenancyAreaSqm: string | null;
  isVacant: boolean | null;
}

interface TenancyFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  tenancy?: Tenancy | null;
  onSuccess?: () => void;
}

// Tenant type enum options - friendly labels
const TENANT_TYPE_OPTIONS = [
  { value: "federal_government", label: "Federal Government" },
  { value: "state_government", label: "State Government" },
  { value: "local_government", label: "Local Government" },
  { value: "listed_public_company", label: "Listed Public Company" },
  { value: "private_company", label: "Private Company" },
  { value: "multinational_corporation", label: "Multinational Corporation" },
  { value: "franchise_national", label: "National Franchise" },
  { value: "franchise_local", label: "Local Franchise" },
  { value: "national_retailer", label: "National Retailer" },
  { value: "major_bank", label: "Major Bank" },
  { value: "supermarket_chain", label: "Supermarket Chain" },
  { value: "fast_food_chain", label: "Fast Food Chain" },
  { value: "restaurant_cafe", label: "Restaurant/Cafe" },
  { value: "hotel_motel", label: "Hotel/Motel" },
  { value: "medical_health", label: "Medical/Health" },
  { value: "legal_accounting", label: "Legal/Accounting" },
  { value: "real_estate_agency", label: "Real Estate Agency" },
  { value: "child_care_centre", label: "Child Care Centre" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "warehousing_logistics", label: "Warehousing/Logistics" },
  { value: "automotive", label: "Automotive" },
  { value: "sme_business", label: "SME Business" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "residential_mixed_use", label: "Residential Mixed Use" },
  { value: "vacant", label: "Vacant" },
  { value: "owner_occupied", label: "Owner Occupied" },
  { value: "corporate", label: "Corporate" },
  { value: "individual", label: "Individual" },
];

export function TenancyFormModal({
  open,
  onOpenChange,
  securityId,
  tenancy,
  onSuccess,
}: TenancyFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!tenancy;

  const form = useForm<TenancyFormData>({
    resolver: zodResolver(tenancyFormSchema),
    defaultValues: {
      tenantName: "",
      tenantType: "",
      tenantAbn: "",
      leaseStartDate: new Date().toISOString().split("T")[0],
      leaseEndDate: "",
      currentRentAnnual: "",
      tenancyAreaSqm: "",
      isVacant: false,
    },
  });

  // Reset form when tenancy changes or modal opens
  useEffect(() => {
    if (open) {
      if (tenancy) {
        form.reset({
          tenantName: tenancy.tenantName || "",
          tenantType: tenancy.tenantType || "",
          tenantAbn: tenancy.tenantAbn || "",
          leaseStartDate: tenancy.leaseStartDate || "",
          leaseEndDate: tenancy.leaseEndDate || "",
          currentRentAnnual: tenancy.currentRentAnnual || "",
          tenancyAreaSqm: tenancy.tenancyAreaSqm || "",
          isVacant: tenancy.isVacant || false,
        });
      } else {
        form.reset({
          tenantName: "",
          tenantType: "",
          tenantAbn: "",
          leaseStartDate: new Date().toISOString().split("T")[0],
          leaseEndDate: "",
          currentRentAnnual: "",
          tenancyAreaSqm: "",
          isVacant: false,
        });
      }
    }
  }, [open, tenancy, form]);

  const createMutation = useMutation({
    mutationFn: async (data: TenancyFormData) => {
      const endpoint = isEditing
        ? `/api/crm/securities/${securityId}/tenancies/${tenancy!.id}`
        : `/api/crm/securities/${securityId}/tenancies`;
      const method = isEditing ? "PATCH" : "POST";

      const response = await apiRequest(method, endpoint, data);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to save tenancy");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}/tenancies`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/crm/securities/${securityId}`],
      });
      toast({
        title: "Success",
        description: `Tenancy ${isEditing ? "updated" : "added"} successfully`,
      });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: TenancyFormData) => {
    // Clean up empty strings to undefined for optional fields
    // Normalize ABN by stripping spaces/dashes
    // For vacant tenancies without end date, use a far-future placeholder (API requires leaseEndDate)
    let leaseEndDate = data.leaseEndDate;
    if (data.isVacant && (!leaseEndDate || leaseEndDate.trim() === "")) {
      // Use end of current year + 1 as placeholder for vacant tenancies
      const currentYear = new Date().getFullYear();
      leaseEndDate = `${currentYear + 1}-12-31`;
    }

    const cleanedData = {
      ...data,
      tenantAbn: data.tenantAbn ? normalizeAbn(data.tenantAbn) : undefined,
      tenancyAreaSqm: data.tenancyAreaSqm || undefined,
      leaseEndDate: leaseEndDate || undefined,
    };
    createMutation.mutate(cleanedData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Tenancy" : "Add Tenancy"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update" : "Add"} a tenancy for this commercial property
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tenantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tenant Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. BWS Liquor" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tenantType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tenant Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger name="tenantType">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TENANT_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tenantAbn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tenant ABN</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., 12345678901"
                      data-testid="tenancy-abn-input"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="leaseStartDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lease Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="leaseEndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lease End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="currentRentAnnual"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Annual Rent (AUD)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="85000.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tenancyAreaSqm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Area (sqm)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="150.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isVacant"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Vacant</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this tenancy as currently vacant
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Save" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
